import React, { useState, useEffect } from 'react';
import Layout from '../../Global/Layout';
import axios from 'axios';
import { showSuccess, showError, showConfirm } from '../../../utils/sweetAlertConfig';
import { ROLE_DISPLAY, API_CONFIG, MSG_REMARK, CUSTOMER_TIER_DISPLAY, CUSTOMER_TIER_DISPLAY_BADGE } from '../../../contexts/constants';
import Select from 'react-select';

const Profile = ({ onLogout, userRole }) => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [formData, setFormData] = useState({
		username: '',
		firstName: '',
		lastName: '',
		phone: '',
		lineID: '',
		promptPay: '',
		promptPayName: '',
		bankName: '',
        bankOtherName: '',
		bankAccountName: '',
		bankAccountNumber: '',
		role: '',
		tier: 'normal',
		currentPassword: '',
		newPassword: '',
		confirmPassword: ''
	});
	const [showPasswordFields, setShowPasswordFields] = useState(false);
	const [passwordError, setPasswordError] = useState('');

	const [bankOptions, setBankOptions] = useState([]);

	useEffect(() => {
		// load banks first so we can map profile bank to bank id
		(async () => {
			const options = await fetchBanks();
			await fetchProfile(options);
		})();
	}, []);

	const fetchBanks = async () => {
		try {
			const token = localStorage.getItem('token');
			const response = await axios.get(`${API_CONFIG.BASE_URL}/banks`, {
				headers: { Authorization: `Bearer ${token}` }
			});

			if (response.data.success && Array.isArray(response.data.data)) {
				const options = response.data.data.map(b => ({ value: b.id, label: b.name }));
				// ensure id 99 (Other) exists as an option
				if (!options.find(o => Number(o.value) === 99)) {
					options.push({ value: 99, label: 'อื่นๆ (ระบุ)' });
				}
				setBankOptions(options);
				return options;
			}
		} catch (error) {
			console.error('Error fetching banks:', error);
				return [];
		}
	};

	if (!['sysadmin', 'owner', 'admin', 'agent', 'customer'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const fetchProfile = async (banks = []) => {
		try {
			setLoading(true);
			const token = localStorage.getItem('token');
			const response = await axios.get(`${API_CONFIG.BASE_URL}/profile`, {
				headers: { Authorization: `Bearer ${token}` }
			});

			if (response.data.success) {
				const profile = response.data.data;
				// use bankId from DB, fallback to mapping bank name if needed
				const lookup = (banks && banks.length) ? banks : bankOptions;
				let bankId = profile.bankId || '';

				// if no bankId but has bankName, try to map it
				if (!bankId && profile.bankName) {
					const match = lookup.find(b => b.label === profile.bankName);
					if (match) bankId = match.value;
					else bankId = 99; // other
				}

				setFormData(prev => ({
					...prev,
					username: profile.username || '',
					firstName: profile.firstName || '',
					lastName: profile.lastName || '',
					phone: profile.phone || '',
					lineID: profile.lineID || '',
					promptPay: profile.promptPay || '',
					promptPayName: profile.promptPayName || '',
					bankName: bankId || '',
					bankOtherName: Number(bankId) === 99 ? profile.bankName || '' : '',
					bankAccountName: profile.bankAccountName || '',
					bankAccountNumber: profile.bankAccountNumber || '',
					role: profile.role || '',
					tier: profile.tier || 'normal'
				}));
			}
		} catch (error) {
			console.error('Error fetching profile:', error);
			showError('ไม่สามารถโหลดข้อมูลโปรไฟล์ได้');
		} finally {
			setLoading(false);
		}
	};

	const handleChange = async (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));

		// Check password match in real-time
		if (name === 'confirmPassword' || name === 'newPassword') {
			const newPass = name === 'newPassword' ? value : formData.newPassword;
			const confirmPass = name === 'confirmPassword' ? value : formData.confirmPassword;

			if (confirmPass && newPass !== confirmPass) {
				setPasswordError('รหัสผ่านไม่ตรงกัน');
			} else {
				setPasswordError('');
			}
		}
	};

	const handleBankChange = (selectedOption) => {
		setFormData(prev => ({
			...prev,
			bankName: selectedOption ? selectedOption.value : '',
			bankOtherName: selectedOption && Number(selectedOption.value) === 99 ? (prev.bankOtherName || '') : ''
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate Line ID - must start with @
		if (formData.lineID && !formData.lineID.startsWith('@')) {
			showError('ไอดีไลน์ต้องขึ้นต้นด้วย @');
			return;
		}

		// Validate bank account number - digits only
		if (formData.bankAccountNumber && !/^\d+$/.test(formData.bankAccountNumber)) {
			showError('เลขที่บัญชีต้องเป็นตัวเลขเท่านั้น');
			return;
		}

		// Validate PromptPay - digits only
		if (formData.promptPay && !/^\d+$/.test(formData.promptPay)) {
			showError('พร้อมเพย์ต้องเป็นตัวเลขเท่านั้น');
			return;
		}

		// Validate bank name when bank_id=99 (Other)
		if (Number(formData.bankName) === 99 && !formData.bankOtherName) {
			showError('กรุณาระบุชื่อธนาคาร');
			return;
		}

		// Validate password fields if changing password
		if (showPasswordFields) {
			if (!formData.currentPassword) {
				showError('กรุณากรอกรหัสผ่านปัจจุบันเพื่อเปลี่ยนรหัสผ่าน');
				return;
			}

			if (!formData.newPassword) {
				showError('กรุณากรอกรหัสผ่านใหม่');
				return;
			}

			if (formData.newPassword !== formData.confirmPassword) {
				showError('รหัสผ่านใหม่และยืนยันรหัสผ่านไม่ตรงกัน');
				return;
			}

			if (formData.newPassword.length < 6) {
				showError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร');
				return;
			}
		}


		const result = await showConfirm({
			title: 'บันทึกการแก้ไข?',
			text: 'คุณต้องการบันทึกการแก้ไขใช่หรือไม่',
			confirmText: 'ตกลง',
			cancelText: 'ยกเลิก',
			confirmVariant: 'primary',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;

		try {
			setSaving(true);
			const token = localStorage.getItem('token');

			const updateData = {
				firstName: formData.firstName,
				lastName: formData.lastName,
				phone: formData.phone,
				lineID: formData.lineID,
				promptPay: formData.promptPay,
				promptPayName: formData.promptPayName,
				bankId: formData.bankName || null,
				bankName: Number(formData.bankName) === 99 ? (formData.bankOtherName || '') : (bankOptions.find(b => b.value === formData.bankName)?.label || ''),
				bankAccountName: formData.bankAccountName,
				bankAccountNumber: formData.bankAccountNumber
			};

			// Add password fields only if changing password
			if (showPasswordFields && formData.newPassword) {
				updateData.currentPassword = formData.currentPassword;
				updateData.newPassword = formData.newPassword;
				updateData.confirmPassword = formData.confirmPassword;
			}

			const response = await axios.put(
				`${API_CONFIG.BASE_URL}/profile`,
				updateData,
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			);

			if (response.data.success) {
				showSuccess('อัปเดตข้อมูลส่วนตัวเรียบร้อยแล้ว');

				// Clear password fields
				setFormData(prev => ({
					...prev,
					currentPassword: '',
					newPassword: '',
					confirmPassword: ''
				}));
				setPasswordError('');
				setShowPasswordFields(false);

				// Refresh profile data
				fetchProfile();
			}
		} catch (error) {
			console.error('Error updating profile:', error);
			const message = error.response?.data?.message || 'ไม่สามารถอัปเดตข้อมูลได้';
			showError(message);
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<Layout title="จัดการข้อมูลส่วนตัว" onLogout={onLogout} userRole={userRole}>
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
						<p className="mt-4 text-gray-600 dark:text-gray-400">กำลังโหลดข้อมูล...</p>
					</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout title="จัดการข้อมูลส่วนตัว" onLogout={onLogout} userRole={userRole}>
			<div className="space-y-6">
				<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
					{/* Header */}
					<div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
						<h2 className="text-xl font-bold text-gray-900 dark:text-white">ข้อมูลส่วนตัว</h2>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">จัดการข้อมูลบัญชีของคุณ</p>
					</div>

					{/* Form */}
					<form onSubmit={handleSubmit} className="p-6 space-y-6">
						{/* Account Information */}
						<div className="space-y-4">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white">ข้อมูลส่วนบุคคล</h3>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{/* Username (Read-only) */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										ชื่อผู้ใช้
									</label>
									<input
										type="text"
										value={formData.username}
										disabled
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
									/>
								</div>

								{/* Role (Read-only) */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										บทบาท
									</label>
									<input
										type="text"
										value={ROLE_DISPLAY[formData.role]}
										disabled
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
									/>
								</div>
							</div>

							{/* Customer Tier (Read-only) - Show only for customers */}
							{formData.role === 'customer' && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											ระดับลูกค้า
										</label>
										<div className="flex items-center h-[42px]">
											<span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold shadow-sm ${CUSTOMER_TIER_DISPLAY_BADGE[formData.tier] || CUSTOMER_TIER_DISPLAY_BADGE.normal}`}>
												{CUSTOMER_TIER_DISPLAY[formData.tier] || 'Normal'}
											</span>
										</div>
									</div>
								</div>
							)}

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{/* First Name */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										ชื่อ <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										name="firstName"
										value={formData.firstName}
										onChange={handleChange}
										required
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										placeholder="กรอกชื่อ"
									/>
								</div>

								{/* Last Name */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										นามสกุล
									</label>
									<input
										type="text"
										name="lastName"
										value={formData.lastName}
										onChange={handleChange}
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										placeholder="กรอกนามสกุล"
									/>
								</div>

								{/* Phone */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										เบอร์โทร <span className="text-red-500">*</span>
									</label>
									<input
										type="tel"
										name="phone"
										value={formData.phone}
										onChange={handleChange}
										required
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										placeholder="0812345678"
									/>
								</div>

								{/* Line ID */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										ไอดีไลน์
									</label>
									<input
										type="text"
										name="lineID"
										value={formData.lineID}
										onChange={handleChange}
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										placeholder="@lineid"
									/>
								</div>
							</div>
						</div>

						{/* Bank Information */}
						<div className="space-y-4">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
								ข้อมูลบัญชีธนาคาร {MSG_REMARK.BANK_PAY}
							</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{/* Bank Name */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										ธนาคาร
									</label>
									<Select
										options={bankOptions}
										value={bankOptions.find(option => option.value === formData.bankName)}
										onChange={handleBankChange}
										isClearable
										placeholder="เลือกธนาคาร"
										className="react-select-container"
										classNamePrefix="react-select"
										styles={{
											control: (base) => ({
												...base,
												backgroundColor: 'transparent',
												borderColor: '#d1d5db',
												'&:hover': { borderColor: '#9ca3af' }
											}),
											menu: (base) => ({
												...base,
												backgroundColor: document.documentElement.classList.contains('dark') ? '#374151' : '#ffffff',
												zIndex: 50
											}),
											option: (base, state) => ({
												...base,
												backgroundColor: state.isFocused
													? (document.documentElement.classList.contains('dark') ? '#4b5563' : '#f3f4f6')
													: 'transparent',
												color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827',
												'&:active': {
													backgroundColor: document.documentElement.classList.contains('dark') ? '#4b5563' : '#e5e7eb'
												}
											}),
											singleValue: (base) => ({
												...base,
												color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827'
											}),
											input: (base) => ({
												...base,
												color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827'
											})
										}}
									/>
								</div>

								{/* Bank Other Name (when selected Other) */}
								{Number(formData.bankName) === 99 && (
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											ระบุชื่อธนาคาร
										</label>
										<input
											type="text"
											name="bankOtherName"
											value={formData.bankOtherName || ''}
											onChange={handleChange}
											className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											placeholder="กรอกชื่อธนาคาร"
										/>
									</div>
								)}
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{/* Bank Account Name */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										ชื่อบัญชี
									</label>
									<input
										type="text"
										name="bankAccountName"
										value={formData.bankAccountName}
										onChange={handleChange}
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										placeholder="ชื่อบัญชีธนาคาร"
									/>
								</div>
								{/* Bank Account Number */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										เลขที่บัญชี
									</label>
									<input
										type="text"
										name="bankAccountNumber"
										value={formData.bankAccountNumber}
										onChange={handleChange}
										onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										placeholder="เลขที่บัญชีธนาคาร"
									/>
								</div>

								{/* PromptPay Name */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										ชื่อบัญชีพร้อมเพย์
									</label>
									<input
										type="text"
										name="promptPayName"
										value={formData.promptPayName}
										onChange={handleChange}
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										placeholder="ชื่อบัญชีพร้อมเพย์"
									/>
								</div>

								{/* PromptPay */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										เลขพร้อมเพย์
									</label>
									<input
										type="text"
										name="promptPay"
										value={formData.promptPay}
										onChange={handleChange}
										onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
										className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										placeholder="เลขบัตรประชาชน / เบอร์โทรศัพท์"
									/>
								</div>
							</div>
						</div>

						{/* Change Password Section */}
						<div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white">เปลี่ยนรหัสผ่าน</h3>
								<button
									type="button"
									onClick={() => {
										setShowPasswordFields(!showPasswordFields);
										// Clear password fields when toggling
										setFormData(prev => ({
											...prev,
											currentPassword: '',
											newPassword: '',
											confirmPassword: ''
										}));
									}}
									className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
								>
									{showPasswordFields ? 'ยกเลิก' : 'เปลี่ยนรหัสผ่าน'}
								</button>
							</div>

							{showPasswordFields && (
								<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
										{/* Current Password */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
												รหัสผ่านปัจจุบัน <span className="text-red-500">*</span>
											</label>
											<input
												type="password"
												name="currentPassword"
												value={formData.currentPassword}
												onChange={handleChange}
												className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
												placeholder="กรอกรหัสผ่านปัจจุบัน"
											/>
										</div>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										{/* New Password */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
												รหัสผ่านใหม่ <span className="text-red-500">*</span>
											</label>
											<input
												type="password"
												name="newPassword"
												value={formData.newPassword}
												onChange={handleChange}
												className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
												placeholder="กรอกรหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)"
											/>
										</div>

										{/* Confirm Password */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
												ยืนยันรหัสผ่านใหม่ <span className="text-red-500">*</span>
											</label>
											<input
												type="password"
												name="confirmPassword"
												value={formData.confirmPassword}
												onChange={handleChange}
												className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
													passwordError
														? 'border-red-500 dark:border-red-500'
														: 'border-gray-300 dark:border-gray-600'
												}`}
												placeholder="กรอกรหัสผ่านใหม่อีกครั้ง"
											/>
											{passwordError && (
												<p className="mt-1 text-xs text-red-600 dark:text-red-400">
													{passwordError}
												</p>
											)}
											{!passwordError && formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
												<p className="mt-1 text-xs text-green-600 dark:text-green-400">
													✓ รหัสผ่านตรงกัน
												</p>
											)}
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Action Buttons */}
						<div className="flex justify-end gap-3 pt-4">
							<button
								type="button"
								onClick={() => {
									fetchProfile();
									setShowPasswordFields(false);
								}}
								className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium"
							>
								ยกเลิก
							</button>
							<button
								type="submit"
								disabled={saving}
								className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition font-medium disabled:cursor-not-allowed flex items-center gap-2"
							>
								{saving ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										กำลังบันทึก...
									</>
								) : (
									'บันทึกข้อมูล'
								)}
							</button>
						</div>
					</form>
				</div>
			</div>
		</Layout>
	);
};

export default Profile;
